<template>
  <v-card class="my-1" elevation="2">
    <v-card-title>
      <h5>Top 10 PSFP ao Longo do Tempo (Heatmap)</h5>
    </v-card-title>

    <v-card-text>
      <div
        v-if="loading"
        class="d-flex align-center justify-center"
        style="height: 200px;"
      >
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <v-simple-table v-else dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th style="min-width: 60px;">Índice</th>
              <th style="min-width: 100px;">Serviço</th>
              <th
                v-for="competencia in competencias"
                :key="competencia"
                class="text-center"
                style="min-width: 80px;"
              >
                {{ formatarCompetencia(competencia) }}
              </th>
              <th class="text-center summary-col">
                PSFP período
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="linha in heatmap" :key="linha.indice_categoria">
              <td>
                <strong>{{ linha.indice_categoria }}</strong>
              </td>

              <td>
                <strong>{{ linha.codigos_servico }}</strong>
                <v-tooltip right>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small class="ml-1" color="grey">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ linha.descricao_por_indice }}</span>
                </v-tooltip>
              </td>

              <td
                v-for="competencia in competencias"
                :key="`${linha.indice_categoria}-${competencia}`"
                class="text-center pa-1"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      class="heatmap-cell"
                      :style="
                        getCellStyle(getItemSerie(linha, competencia).psfp)
                      "
                    >
                      {{
                        getItemSerie(linha, competencia).psfp !== null
                          ? Number(getItemSerie(linha, competencia).psfp)
                              .toFixed(2)
                              .replace(".", ",") + "%"
                          : "—"
                      }}
                    </div>
                  </template>

                  <span>
                    <strong>
                      {{ linha.codigos_servico }} —
                      {{ formatarCompetencia(competencia) }}
                    </strong>
                    <br />
                    PSFP:
                    {{
                      getItemSerie(linha, competencia).psfp !== null
                        ? Number(getItemSerie(linha, competencia).psfp)
                            .toFixed(2)
                            .replace(".", ",") + "%"
                        : "Sem dados"
                    }}
                    <br />
                    SA: {{ getItemSerie(linha, competencia).sa }}
                    <br />
                    SFP: {{ getItemSerie(linha, competencia).sfp }}
                  </span>
                </v-tooltip>
              </td>

              <td class="text-center pa-1 summary-col">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      class="heatmap-cell"
                      :style="getCellStyle(psfpPeriodoResumo(linha))"
                    >
                      {{
                        psfpPeriodoResumo(linha) !== null &&
                          psfpPeriodoResumo(linha) !== undefined
                          ? Number(psfpPeriodoResumo(linha))
                              .toFixed(2)
                              .replace(".", ",") + "%"
                          : "—"
                      }}
                    </div>
                  </template>
                  <span>
                    <strong>{{ linha.codigos_servico }}</strong>
                    <br />
                    PSFP (período):
                    {{
                      psfpPeriodoResumo(linha) !== null &&
                        psfpPeriodoResumo(linha) !== undefined
                        ? Number(psfpPeriodoResumo(linha))
                            .toFixed(2)
                            .replace(".", ",") + "%"
                        : "Sem dados"
                    }}
                    <br />
                    SA: {{ formatarIntPeriodo(linha.sa_periodo) }}
                    <br />
                    SFP: {{ formatarIntPeriodo(linha.sfp_periodo) }}
                  </span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div
        v-if="heatmap.length === 0 && !loading"
        class="text-center grey--text py-4"
      >
        Sem dados para exibição
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "DashboardIsfpPsfpHeatmapTendencia",
  props: {
    heatmap: { type: Array, default: () => [] },
    competencias: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  methods: {
    psfpPeriodoResumo(linha) {
      if (
        linha.psfp_periodo !== null &&
        linha.psfp_periodo !== undefined
      ) {
        return linha.psfp_periodo;
      }
      return linha.psfp_medio;
    },
    formatarIntPeriodo(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return "—";
      }
      return Number(valor).toLocaleString("pt-BR", {
        maximumFractionDigits: 0,
      });
    },
    getItemSerie(linha, competencia) {
      const item = linha.serie?.find((s) => s.competencia === competencia);

      return (
        item || {
          competencia,
          psfp: null,
          sa: 0,
          sfp: 0,
        }
      );
    },
    formatarCompetencia(competencia) {
      const [ano, mes] = competencia.split("-");
      const meses = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      return `${meses[parseInt(mes) - 1]}/${ano.slice(2)}`;
    },

    getCellStyle(psfp) {
      if (psfp === null) {
        return {
          backgroundColor: "#f5f5f5",
          color: "#aaa",
          borderRadius: "6px",
          padding: "6px 8px",
          fontSize: "13px",
        };
      }

      let bg;
      if (psfp <= 1) bg = "#4CAF50";
      else if (psfp <= 5) bg = "#8BC34A";
      else if (psfp <= 10) bg = "#FFC107";
      else if (psfp <= 20) bg = "#FF9800";
      else bg = "#F44336";

      return {
        backgroundColor: bg,
        color: "#fff",
        borderRadius: "6px",
        padding: "6px 8px",
        fontSize: "13px",
        fontWeight: "bold",
      };
    },
  },
};
</script>

<style scoped>
.heatmap-cell {
  display: inline-block;
  min-width: 60px;
  text-align: center;
  cursor: default;
}

.summary-col {
  border-left: 1px solid rgba(0, 0, 0, 0.15) !important;
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
